import { getImitationUserId } from "../lib/authToken";

export type AssessmentType = "reading" | "listening";
export type InitializationStatus = "idle" | "loading" | "success" | "error";

export type TestInitializationState = {
  status: InitializationStatus;
  submissionId: string | null;
  testStatus: string | null;
  message: string | null;
  error: string | null;
};

export type BenchmarkSummary = {
  id: string;
  title: string;
  description?: string | null;
  benchmark_type: "boy" | "moy" | "eoy";
  skill: "reading" | "listening" | "speaking" | "writing";
  updated_at?: string | null;
  start_at?: string | null;
  close_at?: string | null;
  image_url?: string | null;
};

export type BenchmarkImageInput = {
  key: string;
  file_type: string;
};

export type BenchmarkDetail = BenchmarkSummary & {
  district_ids: string[];
  school_ids: string[];
  grade_ids: string[];
};

export type InitializeAssessmentTestParams = {
  apiUrl: string | null | undefined;
  token: string | null;
  testType: AssessmentType;
  me: {
    id?: string | null;
    grade?: string | null;
    district_id?: string | null;
    benchmark_id?: string | null;
    activity_id?: string | null;
  } | null;
  fetchImpl?: typeof fetch;
};

export type FetchAssessmentContentParams = {
  testType: AssessmentType;
  grade?: string;
  isDemo?: boolean;
  submissionId?: string;
  userId?: string;
  includePart?: boolean;
  part?: number;
  location?: string;
  signal?: AbortSignal;
};

type FetchAssessmentContentRequestParams = FetchAssessmentContentParams & {
  apiUrl: string | null | undefined;
  token: string | null;
  fetchImpl?: typeof fetch;
};

type ListAssessmentBenchmarkRequestParams = {
  apiUrl: string | null | undefined;
  token: string | null;
  fetchImpl?: typeof fetch;
  signal?: AbortSignal;
};

type ListAssessmentTestsRequestParams = {
  apiUrl: string | null | undefined;
  token: string | null;
  testType: AssessmentType;
  benchmark?: string;
  fetchImpl?: typeof fetch;
  signal?: AbortSignal;
};

type CreateAssessmentBenchmarkRequestParams = {
  apiUrl: string | null | undefined;
  token: string | null;
  id?: string;
  title: string;
  description?: string;
  benchmark_type: "boy" | "moy" | "eoy";
  skill: "reading" | "listening" | "speaking" | "writing";
  start_date: string;
  end_date: string;
  district_ids: string[];
  school_ids?: string[];
  grade_ids?: string[];
  image?: BenchmarkImageInput;
  remove_image?: boolean;
  fetchImpl?: typeof fetch;
};

export type BenchmarkAssetType = "images" | "audio";

type UploadAssessmentBenchmarkAssetRequestParams = {
  apiUrl: string | null | undefined;
  token: string | null;
  benchmarkId: string;
  assetType: BenchmarkAssetType;
  file: File;
  fetchImpl?: typeof fetch;
  isDemo: boolean,
};

export type UploadedAssessmentBenchmarkAsset = {
  bucket: string;
  key: string;
  publicUrl: string;
};

export type AssessmentBenchmarkAsset = {
  assetType: BenchmarkAssetType;
  bucket: string;
  key: string;
  fileName: string;
  publicUrl: string;
};

type ListAssessmentBenchmarkAssetsRequestParams = {
  apiUrl: string | null | undefined;
  token: string | null;
  benchmarkId: string;
  isDemo: string;
  fetchImpl?: typeof fetch;
  signal?: AbortSignal;
};

type GetAssessmentBenchmarkRequestParams = {
  apiUrl: string | null | undefined;
  token: string | null;
  id: string;
  fetchImpl?: typeof fetch;
  signal?: AbortSignal;
};

type DeleteAssessmentBenchmarkRequestParams = {
  apiUrl: string | null | undefined;
  token: string | null;
  id: string;
  fetchImpl?: typeof fetch;
};

type SaveSubmissionAnswerRequestParams = {
  apiUrl: string | null | undefined;
  token: string | null;
  submissionId: string;
  questionId: string;
  answer: string | null;
  metadata?: object;
  fetchImpl?: typeof fetch;
};

type SaveSubmissionProgressRequestParams = {
  apiUrl: string | null | undefined;
  token: string | null;
  submissionId: string;
  stepKey: string;
  fetchImpl?: typeof fetch;
};

type SaveSubmissionAudioRequestParams = {
  apiUrl: string | null | undefined;
  token: string | null;
  submissionId: string;
  entityType: "oral_reading_audio" | "recap_audio";
  partOrder: number;
  questionId?: string;
  mimeType: string;
  blob: Blob;
  durationMs: number;
  fetchImpl?: typeof fetch;
};

function buildAssessmentHeaders(
  token: string,
  includeContentType = false,
): Record<string, string> {
  const imitationUserId = getImitationUserId()?.trim();

  return {
    Authorization: `Bearer ${token}`,
    ...(includeContentType ? { "Content-Type": "application/json" } : {}),
    ...(imitationUserId ? { ImpersonatedUserId: imitationUserId } : {}),
  };
}

export function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === "AbortError";
}

const initializeAssessmentRequests = new Map<
  string,
  Promise<TestInitializationState>
>();

function buildInitializeAssessmentRequestKey({
  apiUrl,
  testType,
  me,
}: InitializeAssessmentTestParams): string {
  return JSON.stringify([
    apiUrl?.trim() ?? "",
    me?.id?.trim() ?? "",
    me?.grade?.trim() ?? "",
    me?.district_id?.trim() ?? "",
    me?.benchmark_id?.trim() ?? "",
    me?.activity_id?.trim() ?? "",
    testType,
  ]);
}

export function initializeAssessmentTest(
  params: InitializeAssessmentTestParams,
): Promise<TestInitializationState> {
  const requestKey = buildInitializeAssessmentRequestKey(params);
  const existingRequest = initializeAssessmentRequests.get(requestKey);
  if (existingRequest) {
    return existingRequest;
  }

  const request = performInitializeAssessmentTest(params).finally(() => {
    if (initializeAssessmentRequests.get(requestKey) === request) {
      initializeAssessmentRequests.delete(requestKey);
    }
  });
  initializeAssessmentRequests.set(requestKey, request);
  return request;
}

async function performInitializeAssessmentTest({
  apiUrl,
  token,
  testType,
  me,
  fetchImpl = fetch,
}: InitializeAssessmentTestParams): Promise<TestInitializationState> {
  if (!token) {
    throw new Error("Missing auth token.");
  }

  if (!apiUrl) {
    throw new Error("Assessments API URL is not configured.");
  }

  const grade = me?.grade?.trim();
  const userId = me?.id?.trim();
  const districtId = me?.district_id?.trim();
  const benchmarkId = me?.benchmark_id?.trim();
  const activityId = me?.activity_id?.trim();
  if (!grade || !userId) {
    throw new Error("Student grade and id are required.");
  }

  const response = await fetchImpl(`${apiUrl}/assessment`, {
    method: "POST",
    headers: buildAssessmentHeaders(token, true),
    body: JSON.stringify({
      user_id: userId,
      grade,
      test_type: testType,
      ...(districtId ? { district_id: districtId } : {}),
      ...(benchmarkId ? { benchmark_id: benchmarkId } : {}),
      ...(activityId ? { activity_id: activityId } : {}),
    }),
  });

  if (!response.ok) {
    let responseError = `Failed to initialize test (${response.status})`;
    try {
      const errorPayload = (await response.json()) as { error?: string };
      if (errorPayload?.error) {
        responseError = `${responseError}: ${errorPayload.error}`;
      }
    } catch {
      // Ignore unreadable error payloads and preserve the status-only error.
    }
    throw new Error(responseError);
  }

  const data = (await response.json()) as {
    submission_id?: string;
    status?: string;
    message?: string;
  };

  return {
    status: "success",
    submissionId: data?.submission_id ?? null,
    testStatus: data?.status ?? null,
    message: data?.message ?? null,
    error: null,
  };
}

export async function fetchAssessmentContentRequest<T = unknown>({
  apiUrl,
  token,
  testType,
  grade,
  isDemo = false,
  submissionId,
  userId,
  includePart = false,
  part = 1,
  signal,
  fetchImpl = fetch,
}: FetchAssessmentContentRequestParams): Promise<T | null> {
  if (!token) {
    throw new Error("Missing auth token.");
  }
  if (!apiUrl) {
    throw new Error("Assessments API URL is not configured.");
  }
  if (includePart && !submissionId?.trim() && !(isDemo && grade?.trim())) {
    throw new Error("Missing submission id or demo grade for part fetch.");
  }

  const url = new URL(`${apiUrl}/assessment`);
  url.searchParams.set("testType", testType);
  if (grade?.trim()) {
    url.searchParams.set("grade", grade.trim());
  }
  if (isDemo) {
    url.searchParams.set("isDemo", "true");
  }
  if (submissionId?.trim()) {
    url.searchParams.set("submissionId", submissionId.trim());
  }
  if (userId?.trim()) {
    url.searchParams.set("userId", userId.trim());
  }
  if (includePart) {
    url.searchParams.set("includePart", "true");
    url.searchParams.set("part", String(part));
  }

  const response = await fetchImpl(url.toString(), {
    method: "GET",
    headers: buildAssessmentHeaders(token),
    signal,
  });

  if (!response.ok) {
    throw new Error(`Unable to load assessment content (${response.status})`);
  }

  const payload = (await response.json()) as {
    assessment?: T | null;
  };

  return payload.assessment ?? null;
}

export async function listAssessmentBenchmarksRequest({
  apiUrl,
  token,
  fetchImpl = fetch,
  signal,
}: ListAssessmentBenchmarkRequestParams): Promise<BenchmarkDetail[]> {
  if (!token) {
    throw new Error("Missing auth token.");
  }
  if (!apiUrl) {
    throw new Error("Assessments API URL is not configured.");
  }

  const response = await fetchImpl(`${apiUrl}/benchmark`, {
    method: "GET",
    headers: buildAssessmentHeaders(token),
    signal,
  });

  if (!response.ok) {
    throw new Error(
      `Unable to load assessment benchmarks (${response.status})`,
    );
  }

  const payload = (await response.json()) as {
    benchmarks?: BenchmarkDetail[];
  };

  return payload.benchmarks ?? [];
}

export async function listAssessmentTestsRequest({
  apiUrl,
  token,
  testType,
  benchmark,
  fetchImpl = fetch,
  signal,
}: ListAssessmentTestsRequestParams): Promise<
  Array<{
    pk: string;
    sk: string;
    entity_type: string;
    grade: string;
    test_type: string;
    status?: string | null;
    benchmark?: string | null;
    updated_at?: string | null;
    question_count: number;
  }>
> {
  if (!token) {
    throw new Error("Missing auth token.");
  }
  if (!apiUrl) {
    throw new Error("Assessments API URL is not configured.");
  }

  const contentUrl = new URL(`${apiUrl}/content/${testType}`);
  if (benchmark?.trim()) {
    contentUrl.searchParams.set("benchmark", benchmark.trim());
  }

  const response = await fetchImpl(contentUrl.toString(), {
    method: "GET",
    headers: buildAssessmentHeaders(token),
    signal,
  });

  if (!response.ok) {
    throw new Error(`Unable to load ${testType} tests (${response.status})`);
  }

  const payload = (await response.json()) as {
    tests?: Array<{
      pk: string;
      sk: string;
      entity_type: string;
      grade: string;
      test_type: string;
      status?: string | null;
      benchmark?: string | null;
      updated_at?: string | null;
      question_count: number;
    }>;
  };

  return payload.tests ?? [];
}

export async function createAssessmentBenchmarkRequest({
  apiUrl,
  token,
  id,
  title,
  description = "",
  benchmark_type,
  skill,
  start_date,
  end_date,
  district_ids,
  school_ids = [],
  grade_ids = [],
  image,
  remove_image = false,
  fetchImpl = fetch,
}: CreateAssessmentBenchmarkRequestParams): Promise<BenchmarkSummary> {
  if (!token) {
    throw new Error("Missing auth token.");
  }
  if (!apiUrl) {
    throw new Error("Assessments API URL is not configured.");
  }

  const response = await fetchImpl(`${apiUrl}/benchmark`, {
    method: "POST",
    headers: buildAssessmentHeaders(token, true),
    body: JSON.stringify({
      ...(id?.trim() ? { id: id.trim() } : {}),
      title,
      description,
      benchmark_type,
      skill,
      start_date,
      end_date,
      district_ids,
      school_ids,
      grade_ids,
      ...(image ? { image } : {}),
      ...(remove_image ? { remove_image: true } : {}),
    }),
  });

  if (!response.ok) {
    let responseError = `Unable to save assessment version (${response.status})`;
    try {
      const errorPayload = (await response.json()) as { error?: string };
      if (errorPayload?.error) {
        responseError = errorPayload.error;
      }
    } catch {
      // Ignore unreadable error payloads.
    }
    throw new Error(responseError);
  }

  return (await response.json()) as BenchmarkSummary;
}

export async function uploadAssessmentBenchmarkAssetRequest({
  apiUrl,
  token,
  benchmarkId,
  assetType,
  file,
  fetchImpl = fetch,
  isDemo,
}: UploadAssessmentBenchmarkAssetRequestParams): Promise<UploadedAssessmentBenchmarkAsset> {
  if (!token) {
    throw new Error("Missing auth token.");
  }
  if (!apiUrl) {
    throw new Error("Assessments API URL is not configured.");
  }

  const normalizedBenchmarkId = benchmarkId.trim();
  const normalizedMimeType = file.type.trim().toLowerCase();
  if (!normalizedBenchmarkId || !file.name.trim() || !normalizedMimeType) {
    throw new Error("Benchmark asset upload input is invalid.");
  }

  const issueResponse = await fetchImpl(
    `${apiUrl}/benchmark/assets/upload-url`,
    {
      method: "POST",
      headers: buildAssessmentHeaders(token, true),
      body: JSON.stringify({
        benchmark_id: normalizedBenchmarkId,
        asset_type: assetType,
        file_name: file.name,
        mime_type: normalizedMimeType,
        is_demo: isDemo,
      }),
    },
  );

  if (!issueResponse.ok) {
    throw new Error(
      `Unable to issue benchmark asset upload url (${issueResponse.status})`,
    );
  }

  const issuePayload = (await issueResponse.json()) as {
    bucket?: string;
    key?: string;
    public_url?: string;
    put_url?: string;
    headers?: Record<string, string>;
  };

  const bucket = issuePayload.bucket?.trim();
  const key = issuePayload.key?.trim();
  const publicUrl = issuePayload.public_url?.trim();
  const putURL = issuePayload.put_url?.trim();
  const uploadHeaders = issuePayload.headers ?? {};
  if (!bucket || !key || !publicUrl || !putURL) {
    throw new Error("Benchmark asset upload url response is invalid.");
  }

  const putHeaders = { ...uploadHeaders };
  const signedContentType =
    Object.entries(uploadHeaders).find(
      ([headerName]) => headerName.toLowerCase() === "content-type",
    )?.[1] || normalizedMimeType;
  for (const headerName of Object.keys(putHeaders)) {
    if (headerName.toLowerCase() === "content-type") {
      delete putHeaders[headerName];
    }
  }
  putHeaders["Content-Type"] = signedContentType;

  const putResponse = await fetchImpl(putURL, {
    method: "PUT",
    headers: putHeaders,
    body: file,
  });
  if (!putResponse.ok) {
    throw new Error(
      `Unable to upload asset to storage (${putResponse.status})`,
    );
  }

  return { bucket, key, publicUrl };
}

export async function listAssessmentBenchmarkAssetsRequest({
  apiUrl,
  token,
  benchmarkId,
  isDemo,
  fetchImpl = fetch,
  signal,
}: ListAssessmentBenchmarkAssetsRequestParams): Promise<
  AssessmentBenchmarkAsset[]
> {
  if (!token) {
    throw new Error("Missing auth token.");
  }
  if (!apiUrl) {
    throw new Error("Assessments API URL is not configured.");
  }

  const normalizedBenchmarkId = benchmarkId.trim();
  if (!normalizedBenchmarkId) {
    return [];
  }

  const response = await fetchImpl(
    `${apiUrl}/benchmark/assets?benchmark_id=${encodeURIComponent(
      normalizedBenchmarkId,
    )}${isDemo ? `&is_demo=${encodeURIComponent(isDemo)}` : ""}`,
    {
      method: "GET",
      headers: buildAssessmentHeaders(token),
      signal,
    },
  );

  if (!response.ok) {
    throw new Error(`Unable to list benchmark assets (${response.status})`);
  }

  const payload = (await response.json()) as {
    assets?: Array<{
      asset_type?: string;
      bucket?: string;
      key?: string;
      file_name?: string;
      public_url?: string;
    }>;
  };

  return (payload.assets ?? [])
    .map((asset) => ({
      assetType:
        asset.asset_type === "audio" ? ("audio" as const) : ("images" as const),
      bucket: asset.bucket?.trim() ?? "",
      key: asset.key?.trim() ?? "",
      fileName: asset.file_name?.trim() ?? "",
      publicUrl: asset.public_url?.trim() ?? "",
    }))
    .filter((asset) => asset.key && asset.publicUrl);
}

export async function getAssessmentBenchmarkRequest({
  apiUrl,
  token,
  id,
  fetchImpl = fetch,
  signal,
}: GetAssessmentBenchmarkRequestParams): Promise<BenchmarkDetail> {
  if (!token) {
    throw new Error("Missing auth token.");
  }
  if (!apiUrl) {
    throw new Error("Assessments API URL is not configured.");
  }

  const benchmarkId = id.trim();
  if (!benchmarkId) {
    throw new Error("Benchmark id is required.");
  }

  const url = new URL(`${apiUrl}/benchmark`);
  url.searchParams.set("id", benchmarkId);

  const response = await fetchImpl(url.toString(), {
    method: "GET",
    headers: buildAssessmentHeaders(token),
    signal,
  });

  if (!response.ok) {
    let responseError = `Unable to load assessment benchmark (${response.status})`;
    try {
      const errorPayload = (await response.json()) as { error?: string };
      if (errorPayload?.error) {
        responseError = errorPayload.error;
      }
    } catch {
      // Ignore unreadable error payloads.
    }
    throw new Error(responseError);
  }

  return (await response.json()) as BenchmarkDetail;
}

export async function deleteAssessmentBenchmarkRequest({
  apiUrl,
  token,
  id,
  fetchImpl = fetch,
}: DeleteAssessmentBenchmarkRequestParams): Promise<void> {
  if (!token) {
    throw new Error("Missing auth token.");
  }
  if (!apiUrl) {
    throw new Error("Assessments API URL is not configured.");
  }

  const benchmarkId = id.trim();
  if (!benchmarkId) {
    throw new Error("Benchmark id is required.");
  }

  const url = new URL(`${apiUrl}/benchmark`);
  url.searchParams.set("id", benchmarkId);

  const response = await fetchImpl(url.toString(), {
    method: "DELETE",
    headers: buildAssessmentHeaders(token),
  });

  if (!response.ok) {
    let responseError = `Unable to delete assessment benchmark (${response.status})`;
    try {
      const errorPayload = (await response.json()) as { error?: string };
      if (errorPayload?.error) {
        responseError = errorPayload.error;
      }
    } catch {
      // Ignore unreadable error payloads.
    }
    throw new Error(responseError);
  }
}

export async function saveSubmissionAnswerRequest({
  apiUrl,
  token,
  submissionId,
  questionId,
  answer,
  metadata,
  fetchImpl = fetch,
}: SaveSubmissionAnswerRequestParams): Promise<void> {
  if (!token) {
    throw new Error("Missing auth token.");
  }
  if (!apiUrl) {
    throw new Error("Assessments API URL is not configured.");
  }

  const normalizedSubmissionID = submissionId.trim();
  const normalizedQuestionID = questionId.trim();
  const normalizedAnswer =
    typeof answer === "string" ? answer.trim() : (answer ?? null);
  if (!normalizedSubmissionID || !normalizedQuestionID) {
    throw new Error("submissionId and questionId are required.");
  }

  const response = await fetchImpl(`${apiUrl}/submissions`, {
    method: "POST",
    headers: buildAssessmentHeaders(token, true),
    body: JSON.stringify({
      submission_id: normalizedSubmissionID,
      question_id: normalizedQuestionID,
      answer: normalizedAnswer,
      ...(metadata && Object.keys(metadata).length > 0 ? { metadata } : {}),
    }),
  });

  if (!response.ok) {
    throw new Error(`Unable to save submission answer (${response.status})`);
  }
}

export async function saveSubmissionProgressRequest({
  apiUrl,
  token,
  submissionId,
  stepKey,
  fetchImpl = fetch,
}: SaveSubmissionProgressRequestParams): Promise<void> {
  if (!token) {
    throw new Error("Missing auth token.");
  }
  if (!apiUrl) {
    throw new Error("Assessments API URL is not configured.");
  }

  const normalizedSubmissionID = submissionId.trim();
  const normalizedStepKey = stepKey.trim();
  if (!normalizedSubmissionID || !normalizedStepKey) {
    throw new Error("submissionId and stepKey are required.");
  }

  const response = await fetchImpl(`${apiUrl}/submissions/progress`, {
    method: "POST",
    headers: buildAssessmentHeaders(token, true),
    body: JSON.stringify({
      submission_id: normalizedSubmissionID,
      step_key: normalizedStepKey,
    }),
  });

  if (!response.ok) {
    throw new Error(`Unable to save submission progress (${response.status})`);
  }
}

export async function saveSubmissionAudioRequest({
  apiUrl,
  token,
  submissionId,
  entityType,
  partOrder,
  questionId,
  mimeType,
  blob,
  durationMs,
  fetchImpl = fetch,
}: SaveSubmissionAudioRequestParams): Promise<void> {
  if (!token) {
    throw new Error("Missing auth token.");
  }
  if (!apiUrl) {
    throw new Error("Assessments API URL is not configured.");
  }

  const normalizedSubmissionID = submissionId.trim();
  const normalizedEntityType = entityType.trim() as
    | "oral_reading_audio"
    | "recap_audio";
  const normalizedMimeType = mimeType.trim().toLowerCase();
  const normalizedQuestionID = questionId?.trim();

  if (
    !normalizedSubmissionID ||
    !normalizedEntityType ||
    !normalizedMimeType ||
    !blob.size ||
    partOrder <= 0
  ) {
    throw new Error("submission audio upload input is invalid.");
  }

  const issueResponse = await fetchImpl(
    `${apiUrl}/submissions/audio/upload-url`,
    {
      method: "POST",
      headers: buildAssessmentHeaders(token, true),
      body: JSON.stringify({
        submission_id: normalizedSubmissionID,
        entity_type: normalizedEntityType,
        part_order: partOrder,
        ...(normalizedQuestionID ? { question_id: normalizedQuestionID } : {}),
        mime_type: normalizedMimeType,
        file_size_bytes: blob.size,
      }),
    },
  );

  if (!issueResponse.ok) {
    throw new Error(
      `Unable to issue submission audio upload url (${issueResponse.status})`,
    );
  }

  const issuePayload = (await issueResponse.json()) as {
    upload_id?: string;
    put_url?: string;
    audio_url?: string;
    headers?: Record<string, string>;
  };

  const uploadID = issuePayload.upload_id?.trim();
  const putURL = issuePayload.put_url?.trim();
  const audioURL = issuePayload.audio_url?.trim();
  const uploadHeaders = issuePayload.headers ?? {};
  if (!uploadID || !putURL || !audioURL) {
    throw new Error("Submission audio upload url response is invalid.");
  }

  const putResponse = await fetchImpl(putURL, {
    method: "PUT",
    headers: {
      "Content-Type": uploadHeaders["Content-Type"] || normalizedMimeType,
    },
    body: blob,
  });
  if (!putResponse.ok) {
    throw new Error(
      `Unable to upload audio to storage (${putResponse.status})`,
    );
  }

  const completeResponse = await fetchImpl(
    `${apiUrl}/submissions/audio/complete`,
    {
      method: "POST",
      headers: buildAssessmentHeaders(token, true),
      body: JSON.stringify({
        submission_id: normalizedSubmissionID,
        entity_type: normalizedEntityType,
        part_order: partOrder,
        upload_id: uploadID,
        audio_url: audioURL,
        duration_ms: durationMs,
      }),
    },
  );

  if (!completeResponse.ok) {
    throw new Error(
      `Unable to finalize submission audio upload (${completeResponse.status})`,
    );
  }
}
